// AuthContext.js
import React, { useState, useContext, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { jwtDecode } from 'jwt-decode';
import AchievementNotification from './Achievements/AchievementNotification';

const AuthContext = React.createContext();
// Exported for the admin "view as" provider (src/ViewAs), which re-provides
// this context with the viewed teacher or student as the user.
export { AuthContext };

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
// isLoggedIn- Boolean Value set true if user is logged in, false if they are not logged in
// user- Data about the user as shown in aws cognito. 
//               user.email has the email 
//               user.username has the randomly generated cognito username 
//               user.fullName is the full name (old way)
//               user.phoneNumber is the phone number (old way)
//               user.Name is new way to put in name
//               user.phone_Number new way for phone number
//               user.idToken is the ID token from cognito
//               user.role is the role for user (Student or Teacher)
// userData-  An array that is fetched from dynamoDB. Each entry in the array belongs to the user and is a question with all its associated metadata
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Achievement states
  const [allAchievements, setAllAchievements] = useState([]); // All possible achievements
  const [earnedAchievements, setEarnedAchievements] = useState([]); // User's earned achievements
  const [showNotification, setShowNotification] = useState(false);
  const [currentAchievement, setCurrentAchievement] = useState(null);
  const [achievementQueue, setAchievementQueue] = useState([]);

  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      await loadSessionToken();
      setLoading(false);
    };
    initializeAuth();
  }, []);

  // Effect to process achievement queue
  useEffect(() => {
    if (achievementQueue.length > 0 && !showNotification) {
      // Take the first achievement from the queue
      const nextAchievement = achievementQueue[0];
      
      // Remove it from the queue
      setAchievementQueue(prev => prev.slice(1));
      
      // Show notification for this achievement
      setCurrentAchievement(nextAchievement);
      setShowNotification(true);
      
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        dismissAchievementNotification();
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [achievementQueue, showNotification]);

  // Effect to load achievement data after login
  useEffect(() => {
    if (isLoggedIn && user) {
      // Load both all possible achievements and user achievements
      fetchAllAchievements();
      fetchUserAchievements();
    }
  }, [isLoggedIn, user]);

  const handleAPIResponse = async (response, body) => {
    // console.log("Handling API response from ", response);
    if (!response.ok) {
      // Handle errors based on response status
      if (response.status === 403 || response.status === 401) {
        // Token expired, refresh it
        const newAccessToken = await refreshToken();
        // Retry the original request
        return fetch(response.url, {
          method: 'POST',
          headers: {
            ...response.headers,
            Authorization: newAccessToken,
          },
          body: JSON.stringify(body),
        });
      } else {
        throw new Error('Request failed');
      }
    }
    return response;
  };

  // refresh the tokens, dont need auth token and do not need to handle response
  const refreshToken = async () => {
    const response = await fetch('https://brainbuffstutoring.auth.us-east-1.amazoncognito.com/oauth2/token', {
      method: 'POST',
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: 'cdi7icca4pvvm5lcb0jt4ug0k', // webclient app id
        refresh_token: user.refreshToken,
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      if (errorData.error === 'invalid_grant') {
        console.error('Refresh token has expired');
        alert('Your session has expired. You will be signed out for security reasons.');
        logout();
        return;
      }
      console.log(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const updatedContext = { ...user, idToken: data.id_token, accessToken: data.access_token };
    // console.log("Updated tokens:", data.id_token, data.access_token);
    await updateUser(updatedContext);
    return data.access_token;
  };

  const loadSessionToken = async () => {
    try {
      // get cached user credentials
      const sessionToken = localStorage.getItem('sessionToken');
      const parsedToken = JSON.parse(sessionToken);
      if (sessionToken) {
        
        setIsLoggedIn(true);
        setUser(parsedToken);
        // setUser(JSON.parse(sessionToken)); // Assuming sessionToken is a JSON string
      }
      // get cached User Data
      const cachedUserData = localStorage.getItem('userData');
      if (cachedUserData) {
        setUserData(JSON.parse(cachedUserData)); // Assuming sessionToken is a JSON string
      } else if (parsedToken.username) {
        await fetchDataFromDynamoDB();
      }
      
      // Load cached achievements
      const cachedEarnedAchievements = localStorage.getItem('earnedAchievements');
      if (cachedEarnedAchievements) {
        setEarnedAchievements(JSON.parse(cachedEarnedAchievements));
      }
      
      const cachedAllAchievements = localStorage.getItem('allAchievements');
      if (cachedAllAchievements) {
        setAllAchievements(JSON.parse(cachedAllAchievements));
      }
    } catch (error) {
      // console.log('Error loading session token:', error);
    }
  };

  const fetchDataFromDynamoDB = async () => {
    const apiGatewayEndpoint = 'https://fm407nxajh.execute-api.us-west-2.amazonaws.com/getUserData'; // Replace with your API Gateway endpoint

    const requestData = {
      // username: usernamelocal,
      func: "getData",
    };

    // Add role field only if user is a teacher
  if (user && user.role === "Teacher") {
    requestData.role = "Teacher";
  }
    // loadSessionToken and login call this right after setUser, while `user`
    // is still null here (audit W3, 2026-10-19). With no token there is
    // nothing to send; reading user.accessToken threw and logged an error on
    // every page load.
    if (!user?.accessToken) return;
    try {
      const response = await fetch(apiGatewayEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', // Specify that you're sending JSON data
          'Authorization': user.accessToken
        },
        body: JSON.stringify( requestData ), // Convert the data object to a JSON string
      });

      const handledResponse = await handleAPIResponse(response, requestData);

      if (handledResponse.ok) {
        const data = await handledResponse.json();
        setUserData(data);
        // Handle the retrieved data as needed
        
        // Store user data in local storage
        localStorage.setItem('userData', JSON.stringify(data));
      } else {
        console.error('Failed to fetch data from API Gateway');
        // Handle the error case
      }
    } catch (error) {
      console.error('Error fetching data from API Gateway:', error);
    }
  };

  const fetchUserDataFromDynamo = async () => {
    const apiGatewayEndpoint = 'https://fm407nxajh.execute-api.us-west-2.amazonaws.com/getUserData'; // Replace with your API Gateway endpoint

    const requestData = {
      func: "getData",
    };

    if (user && user.role === "Teacher") {
    requestData.role = "Teacher";
  }

    try {
      const response = await fetch(apiGatewayEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', // Specify that you're sending JSON data
          'Authorization': user.accessToken
        },
        body: JSON.stringify( requestData ), // Convert the data object to a JSON string
      });

      const handledResponse = await handleAPIResponse(response, requestData);

      if (handledResponse.ok) {
        const data = await handledResponse.json();
        setUserData(data);
        localStorage.setItem('userData', JSON.stringify(data));
        return data; // Return the fetched data
      } else {
        console.error('Failed to fetch data from API Gateway');
        return null;
        // Handle the error case
      }
    } catch (error) {
      console.error('Error fetching data from API Gateway:', error);
      return null;
    }
  };


  const login = async (credentials) => {
    setIsLoggedIn(true);
    setUser(credentials);
    localStorage.setItem('sessionToken', JSON.stringify(credentials));

    // Fetch data from DynamoDB upon login
    const username = credentials.username;
    const accessToken = credentials.accessToken;
    if (username) {
      await fetchDataFromDynamoDB();
      // Fetch achievements data after login
      await fetchAllAchievements();
      await fetchUserAchievements();
    }
  };

  const logout = async () => {
    await Auth.signOut();
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('earnedAchievements');
    localStorage.removeItem('allAchievements');

    setIsLoggedIn(false);
    setUser(null);
    setUserData([]);
    setEarnedAchievements([]);
    setAllAchievements([]);
    // Straight back to the landing page (Sandy 2026-08-14) — the old
    // "/SuccessfulSignOut" interstitial is gone.
    window.location.href = '/';
  };

  const updateUser = async (newValue) => {
    setUser(newValue);
    localStorage.setItem('sessionToken', JSON.stringify(newValue));
  };

  const updateUserData = async (newValue) => {
    setUserData(newValue);
    localStorage.setItem('userData', JSON.stringify(newValue));

    // Check for achievements after user data is updated
    if (isLoggedIn && user) {
      fetchUserAchievements();
    }
  };
  
  // Achievement functions
  // DISABLED (Sandy 2026-08-10): achievements are paused — no lookups, no
  // notifications. Flip this back to true to re-enable everything (the
  // account-page section is also commented out in AccountManagement.jsx).
  const ACHIEVEMENTS_ENABLED = false;

  // Fetch all possible achievements
  const fetchAllAchievements = async () => {
    if (!ACHIEVEMENTS_ENABLED) return;
    if (!isLoggedIn || !user) return;
    
    try {
      const apiGatewayEndpoint = 'https://m7ro7uu2pk.execute-api.us-west-2.amazonaws.com/getAchievements';
      
      const response = await fetch(apiGatewayEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': user.accessToken
        }
      });
      
      const handledResponse = await handleAPIResponse(response);
      
      if (handledResponse.ok) {
        const data = await handledResponse.json();
        
        if (data.success && data.achievements) {
          setAllAchievements(data.achievements);
          // localStorage.setItem('allAchievements', JSON.stringify(data.achievements));
        }
      }
    } catch (error) {
      console.error('Error fetching all achievements:', error);
    }
  };
  
  // Fetch user's earned achievements and handle new achievements
  const fetchUserAchievements = async () => {
    if (!ACHIEVEMENTS_ENABLED) return;
    if (!isLoggedIn || !user) return;
    
    try {
      const apiGatewayEndpoint = 'https://m7ro7uu2pk.execute-api.us-west-2.amazonaws.com/getUserAchievements';
      
      const response = await fetch(apiGatewayEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.accessToken
        },
      });
      
      const handledResponse = await handleAPIResponse(response);
      
      if (handledResponse.ok) {
        const data = await handledResponse.json();
        
        if (data.success) {
          // Update earned achievements
          if (data.earnedAchievements) {
            setEarnedAchievements(data.earnedAchievements, data.newAchievements);
            // localStorage.setItem('earnedAchievements', JSON.stringify(data.earnedAchievements));
          }
          
          // Handle new achievements
          if (data.newAchievements && data.newAchievements.length > 0) {
            console.log('New achievements detected:', data.newAchievements);
            // Add new achievements to the notification queue
            setAchievementQueue(prev => {
              const prevIds = new Set(prev.map(achievement => achievement.id));
              const uniqueNewAchievements = data.newAchievements.filter(
                achievement => !prevIds.has(achievement.id)
              );
              
              return [...prev, ...uniqueNewAchievements];
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user achievements:', error);
    }
  };
  
  // For testing - function to manually add an achievement to the queue
  const triggerAchievementNotification = (achievement) => {
    setAchievementQueue(prev => [...prev, achievement]);
  };
  
  // Dismiss the current achievement notification
  const dismissAchievementNotification = () => {
    setShowNotification(false);
  };
  
  return (
    <AuthContext.Provider value={{ 
      // Auth values
      isLoggedIn, 
      user, 
      userData, 
      updateUser, 
      updateUserData, 
      login, 
      logout, 
      fetchDataFromDynamoDB,
      handleAPIResponse,
      refreshToken,
      fetchUserDataFromDynamo,
      
      // Achievement values
      allAchievements,
      earnedAchievements,
      fetchUserAchievements,
      fetchAllAchievements,
      currentAchievement,
      showNotification,
      dismissAchievementNotification,
    }}>
      {!loading && children}
      {/* Achievement Notification */}
      {showNotification && currentAchievement && (
        <AchievementNotification 
          achievement={currentAchievement} 
          onDismiss={dismissAchievementNotification} 
        />
      )}
    </AuthContext.Provider>
  );
};

export default AuthProvider;